/**
 * Recent filter searches, kept in this browser.
 *
 * Only the filter terms are kept (`status:sold date:today`), never the free
 * text: free text is how a name, an email address or a phone number is
 * searched, and those are personal data with no business in localStorage.
 * As a second line, any term that looks like an email address or a phone
 * number is dropped too, whatever key it sits under.
 *
 * A new key; no existing `hawfinch_*` key is reused or renamed.
 */
import { looksPersonal } from '../commandRecent';
import { hasOperators, joinTerms, tokenize } from './query';

export const RECENT_QUERIES_KEY = 'hawfinch_cmdk_queries';
export const RECENT_QUERIES_MAX = 5;

/** The filter terms of a query, with anything personal removed, or '' if none are left. */
export function storableQuery(query: string): string {
  const tokens = tokenize(query);
  if (hasOperators(tokens)) {
    // With OR, dropping a term changes what the rest means, so a query that
    // would lose any (free text, or something personal) is not kept at all.
    const kept = tokens.filter((t) => t.op || (t.key !== null && t.value.trim() !== '' && !looksPersonal(t.raw)));
    if (kept.length !== tokens.length) return '';
    const out = joinTerms(kept.map((t) => (t.op === 'or' ? 'OR' : t.raw)));
    return out.length > 300 ? '' : out;
  }
  return tokens
    .filter((t) => t.key !== null && t.value.trim() !== '')
    .map((t) => t.raw)
    .filter((raw) => !looksPersonal(raw))
    .join(' ')
    .slice(0, 300);
}

export function readRecentQueries(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_QUERIES_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((x): x is string => typeof x === 'string')
      .map(storableQuery)
      .filter(Boolean)
      .slice(0, RECENT_QUERIES_MAX);
  } catch {
    return [];
  }
}

/** Puts a query at the top of the list and returns the new list. */
export function pushRecentQuery(query: string): string[] {
  const kept = storableQuery(query);
  const current = readRecentQueries();
  if (!kept) return current;
  const next = [kept, ...current.filter((q) => q !== kept)].slice(0, RECENT_QUERIES_MAX);
  try {
    localStorage.setItem(RECENT_QUERIES_KEY, JSON.stringify(next));
  } catch {
    /* private mode or full storage: the list just is not remembered */
  }
  return next;
}
